import FieldInstance from './FieldInstance';
import Form from './Form';
import { createError } from '../error';
import FieldBlock from "./FieldBlock";
import { castToBoolean } from "../util";

let recapDefinitionCount = 0;
let recapDefinitions = [];

class RecapDefinition {
  constructor() {
    const data = arguments[0] || {};
    this.id = data.id || null;
    this.active = data.active || null;
    this.name = data.name || '';
    this.name_l10n = data.name_l10n || [];
    this.recap_available_events_start_time = data.recap_available_events_start_time || null;
    this.recap_available_events_end_time = data.recap_available_events_end_time || null;
    this.recap_response_available_start_offset = data.recap_response_available_start_offset || null;
    this.recap_response_available_end_offset = data.recap_response_available_end_offset || null;
    this.pages = data.pages || [];
    this.programs = data.programs || [];
    this.event_types = data.event_types || [];
    this.location_variables = data.location_variables || [];
    this.role_variables = data.role_variables || [];
    this.product_lines = data.product_lines || [];
    this.task_types = data.task_types || [];
    this.locations = data.locations || [];
    this.type = data.type || 'event';
    this.customers = data.customers || [];
    this.adhoc = data.adhoc || false;
    this.allow_before_date = data.allow_before_date || false;
    this.allow_before_date_days = parseInt(data.allow_before_date_days) || 0;
    this.version = data.version || null;
    this.updated_at = data.updated_at || null;
    this.user_role_id = data.user_role_id || null;
    this.current_location = data.current_location || null;
    this.location_custom_fields = data.location_custom_fields || [];
    this.logic_filters_data = data.logic_filters_data || null;
    this.order_id = data.order_id || null;
    this.product_lines_id = parseInt(data.product_lines_id) || null;
    this.task_types_id = parseInt(data.task_types_id) || null;
    this.venue_ids = data.venue_ids || [];
    this.venue_filters = data.venue_filters || [];
    this.apply_to_all_venues = castToBoolean(data.apply_to_all_venues);
    this.venues = data.venues || [];
    this.active_rai_codes = data.active_rai_codes || [];

    this._recap_definition_count = recapDefinitionCount;
    recapDefinitionCount += 1;
    this._instantiated_at = (new Date()).toISOString();

    recapDefinitions.push({
      id: this.id,
      instantiated_at: this._instantiated_at,
      count: this._recap_definition_count,
      recapDefinition: this,
    });
  }

  validTargetsForFieldInstance(fieldInstance) {
    let targets = [];
    if (fieldInstance.form && fieldInstance.form.isRepeatableForm) {
      // First include field instances on this form
      targets = targets.concat(
        fieldInstance.form.items
          .map(item => {
            if (item instanceof FieldInstance) {
              return item;
            }
            // Field Block
            return item.items
          })
          .flat()
      );
    }
    // Include all non-repeatable-form field instances
    targets = targets.concat(this._allFieldInstancesExceptRepeatableForms());
    // Do not allow field instance to target itself
    targets = targets.filter(instance => {
      return instance.id !== fieldInstance.id;
    });
    return targets;
  }

  allFieldInstances() {
    return this.findAllFieldInstances(true);
  }

  _allFieldInstancesExceptRepeatableForms() {
    /**
     * Walk the tree and set up links from each FieldInstance to its
     * containing Form or Page
     */
    const mapFieldInstanceToParentObject = {};
    for (const page of this.pages) {
      for (const pageItem of page.items) {
        if (pageItem instanceof FieldInstance) {
          mapFieldInstanceToParentObject[pageItem.id] = {form: null, page: page};
        } else if (pageItem instanceof Form) {
          for (const formItem of pageItem.items) {
            if (formItem instanceof FieldBlock) {
              for (const fieldInstance of formItem.items) {
                // Though these field instances are technically part of the field block,
                // it's more of a logical denotation. The fields are still on the form, so map it accordingly
                mapFieldInstanceToParentObject[fieldInstance.id] = { fieldBlock: formItem, form: pageItem, page: page };
              }
            } else {
              mapFieldInstanceToParentObject[formItem.id] = { form: pageItem, page: page };
            }
          }
        } else {
          throw createError({
            name: "InvalidPageItemType",
            message: "Page item is not a FieldInstance nor a Form",
          });
        }
      }
    }
    return this.allFieldInstances().filter((instance) => {
      const parents = mapFieldInstanceToParentObject[instance.id];
      return !(parents && parents.form && parents.form.isRepeatableForm);
    });
  }

  validTargetsForFormOrFieldBlock() {
    // Currently anything not inside a repeatable form is a valid logical target for a form or field block
    return this._allFieldInstancesExceptRepeatableForms();
  }

  reindexPages() {
    for (let idx = 0; idx < this.pages.length; idx++) {
      this.pages[idx].index = idx;
    }
  }

  addPage(page) {
    this.pages.push(page);
    this.reindexPages();
  }

  removePage(page) {
    let idx = this.pages.indexOf(page);
    if (idx !== -1) {
      this.pages.splice(idx, 1);
      this.reindexPages();
      return 1;
    }
    return 0;
  }

  removeFieldInstance(fieldInstance) {
    let found = 0;
    let idx;

    // On each page search for items attached to the page first
    for (const page of this.pages) {
      idx = page.items.indexOf(fieldInstance);
      if (idx !== -1) {
        page.items.splice(idx, 1);
        found += 1;
      }

      // Now look at items in the items array for that page
      for (const pageItem of page.items) {
        if (pageItem instanceof Form) {
          idx = pageItem.items.indexOf(fieldInstance);
          if (idx !== -1) {
            pageItem.items.splice(idx, 1);
            found += 1;
          }
          for (const formItem of pageItem.items) {
            if (formItem instanceof FieldBlock) {
              idx = formItem.items.indexOf(fieldInstance);
              if (idx !== -1) {
                formItem.items.splice(idx, 1);
                found += 1;
              }
            }
          }
        }
      }
    }
    return found;
  }

  /**
   * Removes a product from a recap form page.
   *
   * @param productInstance
   * @returns {number}
   */
  removeProductInstance(productInstance) {
    let found = 0;
    let idx;

    // Products are assigned to product forms on pages
    // So removing them requires first finding the page then the product.
    for (let page of this.pages) {
      for (let item of page.items) {
        if (item instanceof Form) {
          idx = item.products.indexOf(productInstance);
          if (idx !== -1) {
            item.products.splice(idx, 1);
            found += 1;
          }
        }
      }
    }
    return found;
  }

  removeForm(form) {
    let found = 0;
    for (let page of this.pages) {
      let idx = page.items.indexOf(form);
      if (idx !== -1) {
        page.items.splice(idx, 1);
        found += 1;
      }
    }
    return found;
  }

  removeFieldBlock(fieldBlock) {
    let found = false;
    for (const page of this.pages) {
      for (const form of page.items) {
        const idx = form.items.indexOf(fieldBlock);
        if (idx > -1) {
          found = true;
          form.items.splice(idx, 1);
          break;
        }
      }
      if (found) {
        break;
      }
    }
    return found;
  }

  findAllFieldInstances(includeFieldsWithinForms = false) {
    const fields = [];
    for (const page of this.pages) {
      for (const item of page.items) {
        if (item instanceof FieldInstance) {
          fields.push(item);
        } else if (item instanceof Form && includeFieldsWithinForms === true) {
          for (const formItem of item.items) {
            if (formItem instanceof FieldInstance) {
              fields.push(formItem);
            } else if (formItem instanceof FieldBlock) {
              for (const fieldBlockItem of formItem.items) {
                fields.push(fieldBlockItem);
              }
            }
          }
        }
      }
    }
    return fields;
  }

  findAllForms() {
    const forms = [];
    for (let page of this.pages) {
      for (let item of page.items) {
        if (item instanceof Form) {
          forms.push(item);
        }
      }
    }
    return forms;
  }

  findAllFieldBlocks() {
    const fieldBlocks = [];
    for (let page of this.pages) {
      for (let item of page.items) {
        if (item instanceof Form) {
          for (const formItem of item.items) {
            if (formItem instanceof FieldBlock) {
              fieldBlocks.push(formItem);
            }
          }
        }
      }
    }
    return fieldBlocks;
  }

  findAllLogicConditions() {
    return this._findInAllLogicGroups(group => group.conditions);
  }

  /**
   * Every logic modifier (a choice field's options modifier), wherever its field is: on a form or in
   * a field block.
   */
  findAllLogicModifiers() {
    return this._findInAllLogicGroups(group => group.modifiers);
  }

  /**
   * What `fromGroup` picks out of every logic group on the recap: those of its forms, field blocks
   * and field instances, in a block or not.
   * @private
   */
  _findInAllLogicGroups(fromGroup) {
    let found = [];
    function fromItem(item) {
      if (item.logic_groups && item.logic_groups.length) {
        return item.logic_groups.map(group => fromGroup(group) || []);
      }
      return [];
    }
    for (const page of this.pages) {
      for (const fieldInstanceOrForm of page.items) {
        // item is a Form or a FieldInstance, both of which may have logic groups
        found = found.concat(fromItem(fieldInstanceOrForm));
        if (fieldInstanceOrForm instanceof Form) {
          // Forms also have field instances and field blocks, both of which may have logic groups
          for (const fieldOrFieldBlock of fieldInstanceOrForm.items) {
            found = found.concat(fromItem(fieldOrFieldBlock));
            if (fieldOrFieldBlock instanceof FieldBlock) {
              // Field Blocks contain fields which may have logic groups
              for (const field of fieldOrFieldBlock.items) {
                found = found.concat(fromItem(field));
              }
            }
          }
        }
      }
    }
    return found.flat();
  }

  findAllValidations() {
    let validations = [];
    for (const page of this.pages) {
      for (const fieldInstanceOrForm of page.items) {
        if (fieldInstanceOrForm instanceof FieldInstance) {
          validations = validations.concat(fieldInstanceOrForm.validations);
        } else {
          // Form
          for (const fieldInstanceOrFieldBlock of fieldInstanceOrForm.items) {
            if (fieldInstanceOrFieldBlock instanceof FieldBlock) {
              for (const fieldInstance of fieldInstanceOrFieldBlock.items) {
                validations = validations.concat(fieldInstance.validations)
              }
            } else {
              // Field Instance
              validations = validations.concat(fieldInstanceOrFieldBlock.validations);
            }
          }
        }
      }
    }
    return validations.flat();
  }

  removeAllLocationVariablesInFieldsAndForms() {
    const items = this.findAllFieldInstances(true).concat(this.findAllForms()).concat(this.findAllFieldBlocks());
    items.forEach(item => {
      item.removeAllLocationVariables();
    });
  }

  removeAllRoleVariablesInFieldsAndForms() {
    const items = this.findAllFieldInstances(true).concat(this.findAllForms());
    items.forEach(item => {
      item.removeAllRoleVariables();
    });
  }

  dateIsWithinAvailableOffsets(date) {
    if (this.recap_response_available_start_offset && this.recap_response_available_start_offset > date) {
      return false;
    }
    return !(this.recap_response_available_end_offset && this.recap_response_available_end_offset < date);
  }

  isStaffRecap() {
    return this.type === 'staff';
  }
}

export default RecapDefinition;
